import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Alert,
  Divider,
  alpha,
  styled
} from '@mui/material';
import {
  LightbulbTwoTone,
  InfoTwoTone,
  WarningAmberTwoTone
} from '@mui/icons-material';
import { DocArticle } from '@/constants/docs';
import VideoBlock from './VideoBlock';

/* ─── Styled ─── */

const StepNumber = styled(Box)(
  ({ theme }) => `
    min-width: 30px;
    height: 30px;
    border-radius: 50%;
    background: ${alpha(theme.colors.primary.main, 0.1)};
    color: ${theme.colors.primary.main};
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: ${theme.typography.pxToRem(13)};
    flex-shrink: 0;
  `
);

const StepRow = styled(Box)(
  ({ theme }) => `
    display: flex;
    gap: ${theme.spacing(2)};
    align-items: flex-start;
    padding: ${theme.spacing(1.5, 0)};

    &:not(:last-of-type) {
      border-bottom: 1px solid ${theme.colors.alpha.black[10]};
    }
  `
);

interface DocArticleViewProps {
  article: DocArticle;
}

function DocArticleView({ article }: DocArticleViewProps) {
  return (
    <Box>
      {/* Başlık + özet */}
      <Typography variant="h3" sx={{ fontWeight: 800, mb: 0.5 }}>
        {article.title}
      </Typography>
      <Typography
        variant="subtitle1"
        color="text.secondary"
        sx={{ mb: 2, textTransform: 'none' }}
      >
        {article.summary}
      </Typography>

      {/* Video */}
      <Box sx={{ mb: 3, maxWidth: 760 }}>
        <VideoBlock videoUrl={article.videoUrl} title={article.title} />
      </Box>

      {/* Adımlar */}
      <Card>
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
            Adım adım
          </Typography>
          <Divider sx={{ mb: 1 }} />
          {article.steps.map((step, i) => (
            <StepRow key={i}>
              <StepNumber>{i + 1}</StepNumber>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {step.text}
                </Typography>
                {step.note && (
                  <Alert
                    icon={<InfoTwoTone fontSize="inherit" />}
                    severity="info"
                    sx={{ mt: 1, textTransform: 'none' }}
                  >
                    {step.note}
                  </Alert>
                )}
                {step.warning && (
                  <Alert
                    icon={<WarningAmberTwoTone fontSize="inherit" />}
                    severity="warning"
                    sx={{ mt: 1, textTransform: 'none' }}
                  >
                    {step.warning}
                  </Alert>
                )}
              </Box>
            </StepRow>
          ))}
        </CardContent>
      </Card>

      {/* İpuçları */}
      {article.tips && article.tips.length > 0 && (
        <Card sx={{ mt: 2 }}>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <LightbulbTwoTone color="warning" />
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                İpuçları
              </Typography>
            </Box>
            <Divider sx={{ mb: 1.5 }} />
            <Box component="ul" sx={{ m: 0, pl: 3 }}>
              {article.tips.map((tip, i) => (
                <Typography
                  key={i}
                  component="li"
                  variant="body2"
                  sx={{ mb: 0.75, textTransform: 'none' }}
                >
                  {tip}
                </Typography>
              ))}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Alt bilgi rozeti */}
      <Box sx={{ mt: 2 }}>
        <Chip
          size="small"
          variant="outlined"
          label={`Konu kimliği: ${article.id}`}
          sx={{ opacity: 0.6 }}
        />
      </Box>
    </Box>
  );
}

export default DocArticleView;
